import AppModalContainer from '@nxsys/components/AppModalContainer';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { toNumber } from 'lodash';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProspectForm, {
  ProspectFormRef,
  ProspectFormValues,
} from './ProspectForm';

type CreateProspectProps = {
  isOpen: boolean;
  toggle: () => void;
};

const CreateProspectModal: React.FC<CreateProspectProps> = ({
  isOpen,
  toggle,
}: CreateProspectProps) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;
  const {
    prospectList: { queryParams },
  } = useSelector((state: AppState) => state.prospectStore);

  const formRef = useRef<ProspectFormRef>(null);

  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (values: ProspectFormValues) => {
    setIsCreating(true);
    try {
      const params = {
        DOB: values.dob ? moment(values.dob).toISOString() : null,
        companyPaymentId: values.paymentCompany?.value ?? null,
        email: values.email ?? null,
        forename: values.forename ?? null,
        payFrequency: values.payFrequency ?? null,
        // UK bureau has no payment model to pick — it is always PAYE
        paymentModel: isUK
          ? [PAYMENT_MODEL_ENUM.PAYEE]
          : values.paymentModel?.value
            ? [values.paymentModel.value]
            : null,
        phone: values.phoneNumber ?? null,
        surname: values.surname ?? null,
        title: values.title?.value ?? null,
        // Business type only belongs to the self-employed models, and neither
        // bureau nor Ireland has one; anything left over from an earlier
        // selection must not be sent
        businessType:
          !isBureau &&
          !isIreland &&
          ['CIS', 'NON_CIS'].includes(values.paymentModel?.value as string)
            ? (values.businessType?.value ?? 'SOLE_TRADER')
            : null,
        payRate: !isBureau && values.payRate ? toNumber(values.payRate) : null,
      };
      // unwrap() so a rejected request throws — without it the thunk resolves
      // and the modal closes as if the create had succeeded
      await dispatch(prospectActions.createProspect(params)).unwrap();
      await dispatch(prospectActions.getProspectList({ ...queryParams }));
      formRef.current?.reset();
      toggle();
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['prospect.addNewProspect']}
      isOpen={isOpen}
      toggle={toggle}
      // Unmount the form on close so nothing typed before carries over — a
      // plain resetFields() leaves AppSelectSync showing its last selection
      destroyOnClose
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isCreating,
          disabled: isCreating,
        },
      ]}
    >
      <ProspectForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateProspectModal;
